import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Button,
  Divider,
  TextField,
  Paper,
  Stack,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import DEMO_CONFIG from "../config/demo";
import dummyData from "../data/dummy-data.json";
import DemoDataManager from "../utils/DemoDataManager";

/**
 * Demo Control Panel
 *
 * Provides controls for managing demo mode during presentations
 * Only visible when demo mode is active
 */
const DemoControlPanel = () => {
  const [open, setOpen] = useState(false);
  const [currentData, setCurrentData] = useState(null);

  useEffect(() => {
    if (open) {
      // Refresh current data when panel opens
      const data = DemoDataManager.getData();
      setCurrentData(data);
    }
  }, [open]);

  if (!DEMO_CONFIG.USE_MOCK_API) {
    return null;
  }

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleResetData = () => {
    // Reset demo data to defaults
    const confirmReset = window.confirm(
      "Reset all demo data to defaults? This will restore original balances and clear transaction history.",
    );
    if (confirmReset) {
      DemoDataManager.resetData();
      const data = DemoDataManager.getData();
      setCurrentData(data);
      // Clear any pending transaction status
      localStorage.removeItem("temp_txn_data");
      alert("Demo data has been reset! Refresh the page to see changes.");
      window.location.reload();
    }
  };

  const handleQuickLogin = () => {
    // Set demo user data in AuthStorage for quick access
    const demoAuthData = {
      token: dummyData.auth.login.token,
      user: dummyData.user,
      wallets: dummyData.wallets,
    };
    localStorage.setItem("authStorage", JSON.stringify(demoAuthData));
    window.location.href = "/dashboard/wallet";
  };

  return (
    <>
      {/* Floating Settings Button */}
      <IconButton
        onClick={toggleDrawer}
        sx={{
          position: "fixed",
          bottom: 80,
          right: 20,
          backgroundColor: "#2196f3",
          color: "white",
          zIndex: 9999,
          "&:hover": {
            backgroundColor: "#1976d2",
          },
        }}
      >
        <SettingsIcon />
      </IconButton>

      {/* Control Panel Drawer */}
      <Drawer anchor="right" open={open} onClose={toggleDrawer}>
        <Box
          sx={{
            width: 350,
            p: 3,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#1a2332" : "#fff",
            color: (theme) => (theme.palette.mode === "dark" ? "#fff" : "#000"),
            minHeight: "100vh",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              🎭 Demo Controls
            </Typography>
            <IconButton
              onClick={toggleDrawer}
              size="small"
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#fff" : "#000",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider
            sx={{
              mb: 3,
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "#3a4556" : "rgba(0,0,0,0.12)",
            }}
          />

          {/* Current Demo Data */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#2a3544" : "#f5f5f5",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#fff" : "#000",
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Current Demo Data
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                💰 Balance:{" "}
                <strong>
                  {currentData
                    ? currentData.wallets.pulsar_data.balance.toFixed(2)
                    : "Loading..."}{" "}
                  PLSR
                </strong>
              </Typography>
              <Typography variant="body2">
                🎯 Staked:{" "}
                <strong>
                  {currentData
                    ? currentData.staking.user_stake_details.data.user.user_stake_balance.toFixed(
                        2,
                      )
                    : "Loading..."}{" "}
                  PLSR
                </strong>
              </Typography>
              <Typography variant="body2">
                📊 Transactions:{" "}
                <strong>
                  {currentData ? currentData.transactions.length : "0"}
                </strong>
              </Typography>
              <Typography variant="body2">
                👤 User:{" "}
                <strong>
                  {currentData ? currentData.user.username : "Loading..."}
                </strong>
              </Typography>
            </Stack>
          </Paper>

          {/* Quick Actions */}
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            gutterBottom
            sx={{
              color: (theme) =>
                theme.palette.mode === "dark" ? "#e0e0e0" : "inherit",
            }}
          >
            Quick Actions
          </Typography>

          <Stack spacing={2} sx={{ mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleQuickLogin}
            >
              Quick Login
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={handleResetData}
            >
              Reset Demo Data
            </Button>
          </Stack>

          <Divider
            sx={{
              mb: 3,
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "#3a4556" : "rgba(0,0,0,0.12)",
            }}
          />

          {/* Demo Info */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#2a3f5f" : "#e3f2fd",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#e3f2fd" : "#01579b",
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              gutterBottom
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#90caf9" : "#01579b",
              }}
            >
              ℹ️ Demo Mode Info
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 1 }}>
              • All data is simulated
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 1 }}>
              • No real transactions occur
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 1 }}>
              • Refresh to reset state
            </Typography>
            <Typography variant="caption" component="div">
              • Works offline
            </Typography>
          </Paper>

          <Divider
            sx={{
              my: 3,
              borderColor: (theme) =>
                theme.palette.mode === "dark" ? "#3a4556" : "rgba(0,0,0,0.12)",
            }}
          />

          {/* Demo Credentials */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#3f3020" : "#fff3e0",
              color: (theme) =>
                theme.palette.mode === "dark" ? "#ffe0b2" : "#e65100",
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              gutterBottom
              sx={{
                color: (theme) =>
                  theme.palette.mode === "dark" ? "#ffb74d" : "#e65100",
              }}
            >
              🔑 Demo Credentials
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
              Email: demo@pulsar.com
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
              Username: demo_user
            </Typography>
            <Typography variant="caption" component="div">
              Password: (any password)
            </Typography>
          </Paper>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Demo Mode v1.0
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default DemoControlPanel;
